import { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';

// Fix Leaflet default marker icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

export default function MapModal({ isOpen, onClose, address2, onSelectLocation }) {
  const { t } = useTranslation();
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [address, setAddress] = useState(address2);
  const [searchAddress, setSearchAddress] = useState(address2);

  useEffect(() => {
    if (!isOpen) return;
    setSearchAddress(address2?address2:'');
    setAddress(address2?address2:'');
    // Initialize map when modal opens
    const mapInstance = L.map('geocoding-map').setView([0, 0], 2);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(mapInstance);

    setMap(mapInstance);

    // Cleanup on modal close
    return () => {
      if (mapInstance) {
        mapInstance.remove();
      }
    };
  }, [isOpen,address2]);

  useEffect(() => {
    if (!map || !address) return;

    // Geocode address using Nominatim
    const geocodeAddress = async () => {
      try {
        const response = await axios.get(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`
        );

        if (response.data && response.data[0]) {
          const { lat, lon } = response.data[0];
          const position = [parseFloat(lat), parseFloat(lon),response.data[0].name];

          // Update map view
          map.setView(position, 16);

          // Update or create marker
          if (marker) {
            marker.setLatLng(position);
          } else {
            const newMarker = L.marker(position, { draggable: true })
              .addTo(map)
              .on('dragend', (e) => {
                setSelectedPosition([e.target.getLatLng().lat, e.target.getLatLng().lng, response.data[0].name]);
              });
            setMarker(newMarker);
          }

          setSelectedPosition(position);
        }
      } catch (error) {
        console.error('Geocoding failed:', error);
      }
    };

    geocodeAddress();
  }, [map, address, marker]);

  const handleConfirm = () => {
    if (selectedPosition) {
      onSelectLocation({
        latitude: selectedPosition[0],
        longitude: selectedPosition[1],
        name: selectedPosition[2]
      });
      onClose();
    }
  };

  const handleSearch = async () => {
    setAddress(searchAddress);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[800px] rounded-lg shadow-xl">
        <div className="bg-black text-white px-4 py-3 rounded-t-lg flex justify-between items-center">
          <h2 className="text-lg font-semibold">
            {t('Select Location')}
          </h2>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              placeholder={t('Search')}
              className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={()=>{handleSearch();}}
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              {t('Search')}
            </button>
          </div>

          <div id="geocoding-map" className="h-[400px] rounded-lg" />

          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              {t('Cancel')}
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              disabled={!selectedPosition}
            >
              {t('Confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}